import './style/InventoryList.css';
import React, { useCallback, useEffect, useMemo } from 'react';
import InventoryHeaderColumn from './InventoryHeaderColumn';
import { useSelector } from 'react-redux';
import { openTrayTypeWithData } from '../../redux/keystoneSlice';
import { useDispatch } from 'react-redux';
import { formatToCurrency, formatToCurrencyExpanded, formatNumberWithCommas } from '../../util/Calculate';
import InfoCircle from '../../media/note24@3x.png';
import { makeSelectInventoryItemById, selectAllInventoryItemsWithData } from '../../redux/keystoneInventorySlice';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GroupedVirtuoso } from 'react-virtuoso';
import HeaderVisual from './HeaderVisual';
import { useEvent as useEffectEvent } from '@reactuses/core';
import { Text } from '@harperai/materialize-design-system';

const columns = [
    "Internal Item",
    "Asking Price",
    "Matched Item",
    "Listing",
];

const InventoryList = ({ searchInputValue }) => {
    const keystoneUserPreferencesSortBy = useSelector((state) => state?.keystone?.userPreferences?.sortBy);
    const { pathname } = useLocation();
    const { page } = useParams();
    const navigate = useNavigate();
    
    const { search } = useLocation();
    const selectedObjectId = new URLSearchParams(search).get('id') || null;

    const selectInventoryItemById = useMemo(() => makeSelectInventoryItemById(), []);
    const selectedObject = useSelector(state => selectInventoryItemById(state, selectedObjectId));
    
    const keystoneInterfaceFilteredInventoryIds = useSelector((state) => state?.keystoneInterface?.filteredInventoryIds);
    const keystoneInventoryAll = useSelector(selectAllInventoryItemsWithData);

    const dispatch = useDispatch();

    const sortedInventoryData = useMemo(() => {
        // TODO: Kill cameron for allowing this to happen
        let filteredInventory = searchInputValue?.length > 0 
            ? [...keystoneInventoryAll || []]?.filter(item => 
                item.mpn?.toLowerCase()?.includes(searchInputValue?.toLowerCase()) 
                || item.manufacturer?.toLowerCase().includes(searchInputValue?.toLowerCase())
                || item.manufacturer?.toLowerCase().includes(searchInputValue?.toLowerCase())
                || item.warehouse_name?.toLowerCase().includes(searchInputValue?.toLowerCase())
                || `${item.quantity ?? ''}`.includes(searchInputValue?.toLowerCase()?.replace(',', '')?.replace('$', ''))
                || `${item.unit_value ?? ''}`.includes(searchInputValue?.toLowerCase()?.replace(',', '')?.replace('$', ''))
                || `${((item.quantity ?? 0) * (item.unit_value ?? 0))}`.includes(searchInputValue?.toLowerCase()?.replace(',', '')?.replace('$', ''))
                || (!item.upleveled_item && !item.shop_listing && item.normalization_status !== 'PENDING_NORMALIZATION' && "No match".toLowerCase().includes(searchInputValue?.toLowerCase()))
                || (!item.upleveled_item && !item.shop_listing && item.normalization_status === 'PENDING_NORMALIZATION' && "Processing".toLowerCase().includes(searchInputValue?.toLowerCase()))
                || (item.upleveled_item && item.upleveled_item?.mpn?.toLowerCase().includes(searchInputValue?.toLowerCase()))
                || (item.upleveled_item && item.upleveled_item?.manufacturer?.toLowerCase().includes(searchInputValue?.toLowerCase()))
                || (item.upleveled_item && item.upleveled_item?.short_description?.toLowerCase().includes(searchInputValue?.toLowerCase()))
                || (item.upleveled_item && item.upleveled_item?.mpn?.toLowerCase().includes(searchInputValue?.toLowerCase()))
                || (item.upleveled_item && item.upleveled_item?.manufacturer?.toLowerCase().includes(searchInputValue?.toLowerCase()))
                || (item.upleveled_item && item.upleveled_item?.short_description?.toLowerCase().includes(searchInputValue?.toLowerCase()))
            )
            : [...keystoneInventoryAll || []];
        filteredInventory = filteredInventory.filter(item => !keystoneInterfaceFilteredInventoryIds?.length || keystoneInterfaceFilteredInventoryIds?.includes(item.id));
        if (filteredInventory?.length > 0 && keystoneUserPreferencesSortBy && columns.includes(keystoneUserPreferencesSortBy.replace("-ascending", ""))) {
            filteredInventory = [...filteredInventory].sort((a, b) => {
                const aValue = keystoneUserPreferencesSortBy?.includes('Score') ? a?.computed?.score_clamped :
                    keystoneUserPreferencesSortBy?.includes('Internal Item') ? a?.mpn?.toLowerCase() :
                    keystoneUserPreferencesSortBy?.includes('Asking Price') ? a?.unit_value * a?.quantity :
                    keystoneUserPreferencesSortBy?.includes('Matched Item') ? a?.shop_listing?.market_mpn ? a?.shop_listing?.market_mpn?.toLowerCase() : a?.upleveled_item?.mpn?.toLowerCase() :
                    keystoneUserPreferencesSortBy?.includes('Listing') ? (a?.shop_listing ? -1 : a?.upleveled_item ? 0 : 1) :
                    undefined;
                const bValue = keystoneUserPreferencesSortBy?.includes('Score') ? b?.computed?.score_clamped :
                    keystoneUserPreferencesSortBy?.includes('Internal Item') ? b?.mpn?.toLowerCase() :
                    keystoneUserPreferencesSortBy?.includes('Asking Price') ? b?.unit_value * b?.quantity : 
                    keystoneUserPreferencesSortBy?.includes('Matched Item') ? b?.shop_listing?.market_mpn ? b?.shop_listing?.market_mpn?.toLowerCase() : b?.upleveled_item?.mpn?.toLowerCase() :
                    keystoneUserPreferencesSortBy?.includes('Listing') ? (b?.shop_listing ? -1 : b?.upleveled_item ? 0 : 1) :
                    undefined;

                if ((aValue === undefined || (typeof aValue !== 'string' && isNaN(aValue))) && (bValue === undefined || (typeof bValue === 'string' && isNaN(bValue)))) {
                    return 0; 
                }
                if (aValue === undefined || (typeof aValue !== 'string' && isNaN(aValue))) {
                    return 1; 
                }
                if (bValue === undefined || (typeof aValue !== 'string' && isNaN(bValue))) {
                    return -1; 
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return aValue.localeCompare(bValue);
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return aValue.localeCompare(bValue);
                } else {
                    return aValue - bValue;
                }
            });

            if (keystoneUserPreferencesSortBy?.includes("-ascending")) {
                filteredInventory.reverse();
            }
        }
        return filteredInventory;
    }, [keystoneInventoryAll, keystoneInterfaceFilteredInventoryIds, keystoneUserPreferencesSortBy, searchInputValue]);

    const navigateOnClick = useEffectEvent((id) => {
        const params = new URLSearchParams(search);
        params.set('id', id);
        navigate(`${page ? `/${page}` : pathname}?${params.toString()}`);
    });

    const headerContent = useMemo(() => (() => (
        <div id="keystone-inventory-header-wrapper">
            <div id="keystone-inventory-header" className="product-horizontal-container">
                {columns.map((column, index) => (
                    <InventoryHeaderColumn column={index + 1} key={index} label={column} flex={index % 2 === 0} lastColumn={index === columns.length - 1} hideMobile={index !== 0 ? index !== columns.length - 1 : false} />
                ))}
            </div>
        </div>)), []);

    const mapIndexToComponent = useCallback(index => {
        const item = sortedInventoryData[index] || {};

        let status = item?.normalization_status;
        // status = "PENDING_NORMALIZATION";

        let hasShopListing = item?.shop_listing !== null;
        // hasShopListing = false;

        let hasUpleveledItem = item?.upleveled_item !== null;
        // hasUpleveledItem = false;

        const manufacturer = hasShopListing ? item?.shop_listing?.market_manufacturer : item?.upleveled_item?.manufacturer;
        const partNumber = hasShopListing ? item?.shop_listing?.market_mpn : item?.upleveled_item?.mpn;
        const description = hasShopListing ? item?.shop_listing?.description : item?.upleveled_item?.short_description;

        return (
            // Row
            <div onClick={() => navigateOnClick(item.id)} className={`flex-1 keystone-card keystone-row ${selectedObjectId === item.id ? "keystone-row-active" : ""} clickable space-between pad-m`} key={item.id || index}>
                {/* Column 1 */}
                <div className='product-vertical-container inventory-column relaxed-width first'>
                    <div className={`product-horizontal-container align-center h-gap-xs`}>
                        <Text header={5} size="paragraph" mono={true} bold={true}>{item.mpn}</Text>
                        <HeaderVisual
                            key={`${item.id || index}-mpn-tooltip`}
                            index={index}
                            displayStyle={false}
                            tooltip={item?.description}
                            icon={InfoCircle}/>
                    </div>
                    <Text header={6} size="micro" mono={true} bold={true} variant="secondary">{`${item.manufacturer} (${item.warehouse_name})`}</Text>
                </div>

                {/* Column 2 */}
                <div className='product-vertical-container align-end inventory-column strict-width'>
                    <Text header={5} size="paragraph" mono={true} bold={true}>{item.unit_value && item.quantity ? formatToCurrency(item.unit_value * item.quantity) : null}</Text>
                    <Text header={6} size="micro" mono={true} bold={true} variant="secondary">{`${formatNumberWithCommas(item?.quantity)} x ${formatToCurrencyExpanded(item?.unit_value)}`}</Text>
                </div>

                {/* Column 3 */}
                <div className='product-horizontal-container align-center inventory-column relaxed-width'>
                    {(hasUpleveledItem || hasShopListing) && (
                    <div className='product-vertical-container'>
                        <div className={`product-horizontal-container align-center h-gap-xs`}>
                            <Text header={5} size="paragraph" mono={true} bold={true}>{partNumber}</Text>
                            <HeaderVisual
                                key={`${item.id || index}-associated-part-tooltip`}
                                index={index}
                                displayStyle={false}
                                tooltip={description}
                                icon={InfoCircle}/>
                        </div>
                        <Text header={6} size="micro" mono={true} bold={true} variant="secondary">{manufacturer}</Text>
                    </div>
                    )}
                    {(status === 'PENDING_NORMALIZATION' && !hasUpleveledItem && !hasShopListing) && (
                        <Text header={6} size="micro" mono={true} bold={true} variant="secondary">Processing</Text>
                    )}
                    {(status !== 'PENDING_NORMALIZATION' && !hasUpleveledItem && !hasShopListing) && (
                        <Text header={6} size="micro" mono={true} bold={true} variant="secondary">No Match</Text>
                    )}
                </div>

                {/* Column 4 */}
                <div className='product-horizontal-container justify-end align-center inventory-column strict-width last'>
                    {(status !== 'PENDING_NORMALIZATION' || hasShopListing || hasUpleveledItem) && (
                        <div className="status-chip flex horizontal grey-bg round-s gap-m">
                            {(hasShopListing || hasUpleveledItem) && (
                                <div class={`small-circle ${hasShopListing ? "circle-positive" : "circle-warning"}`}/>
                            )}
                            <Text size="micro" bold={true} mono={true}>{hasShopListing ? "Live" : hasUpleveledItem ? "In Review" : "Pending"}</Text>
                        </div>
                    )}
                </div>
            </div>);
    }, [sortedInventoryData]);

    const rowContent = useMemo(() => (
        <GroupedVirtuoso
            style={{ }}
            groupCounts={[sortedInventoryData.length]}
            groupContent={headerContent}
            components={{
                List: React.forwardRef(({style, children}, ref) => (
                    <div className="product-vertical-container list-container v-gap-s" style={{...style}} ref={ref}>
                        {children}
                    </div>
                ))
            }}
            itemContent={mapIndexToComponent}
        />),                
        [headerContent, mapIndexToComponent, sortedInventoryData.length]);

    useEffect(() => {
        if (selectedObject) {
            dispatch(openTrayTypeWithData({
                trayOpen: true,
                trayData: selectedObject,
                trayType: "product"
            }));
        }
    }, [dispatch, selectedObject]);
    return (
        <div className="product-vertical-container flex-1">
            {rowContent}
        </div>
    );
};

export default React.memo(InventoryList);
